import {
	Grid,
	InputAdornment,
	Card,
	CardContent,
	InputLabel,
	Typography,
	TextField,
	styled,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { debounce } from 'lodash';

import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import { Loader } from 'common/components/Loader/Loader';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setRegChangeSearchText } from 'common/store/reducer/commonReducer';

import { getRegulationListCount } from '../RegulationChange/api';
import { IRegulationFinalRule } from '../RegulationChange/api/type-def';

import dropdown from '../../../common/assets/images/dropDownIcon.svg';

import RegulationChangeListTable from './RegulationChangeListTable/RegulationChangeListTable';

import styles from './RegulationChangeList.module.scss';

const { searchBorder } = styles;

export const StyledTextField = styled(TextField)({
	background: 'white',
	borderRadius: '4px',
	border: `1px solid ${searchBorder}`,
	width: '40%',
});

interface ISelectedDateRangeProp {
	viewDataForRange: string;
}

const { OK, INTERNAL_SERVER_ERROR } = HTTP_STATUS_CODES;

const RegulationChangeList = (props: ISelectedDateRangeProp) => {
	const dispatch = useDispatch();
	const { viewDataForRange } = props;
	const [toastMessage, setToastMessageLocal] = useState('');
	const [isToastOpen, setIsToastOpen] = useState(false);
	const [finalRuleData, setFinalRuleData] = useState<IRegulationFinalRule>();
	const [isLoading, setLoading] = useState<boolean>(false);
	const [isAPIDataFetched, setIsAPIDataFetched] = useState(false);
	const refSearchText = useSelector(
		(state: RootState) => state.common.regChangeSearchText,
	);

	const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const text = event.target.value;
		dispatch(setRegChangeSearchText(text));
		debouncedSearchHandler(text);
	};
	const debouncedSearchHandler = debounce((text: string) => {
		fetchRegulationListFinalRule(text);
	}, 500);

	const fetchRegulationListFinalRule = async (text: string) => {
		setLoading(true);
		const paramsObj = { searchText: text };
		const resp = await getRegulationListCount(paramsObj);
		if (resp?.statusCode === OK && resp?.responseBody) {
			setLoading(false);
			setFinalRuleData(resp.responseBody);
			setIsAPIDataFetched(true);
		} else if (resp?.statusCode === INTERNAL_SERVER_ERROR) {
			setIsToastOpen(true);
			setToastMessageLocal(APP_GENERAL_ERROR_MSG);
			setFinalRuleData({
				pastDueCount: 0,
				highPriorityCount: 0,
				nearDeadlineCount: 0,
				recentlySent: 0,
			});
			setLoading(false);
		} else {
			setIsToastOpen(true);
			setToastMessageLocal('No Data Found');
			setFinalRuleData({
				pastDueCount: 0,
				highPriorityCount: 0,
				nearDeadlineCount: 0,
				recentlySent: 0,
			});
			setLoading(false);
		}
	};
	useEffect(() => {
		fetchRegulationListFinalRule(refSearchText);
	}, []);

	const handleToast = useCallback(() => {
		setIsToastOpen(false);
		setToastMessageLocal('');
	}, []);

	const displayCardItems = (title: string, displayCount: number) => (
		<Grid item md={3} sm={6}>
			<div className={styles['regulationlist-stats-card-header']}>{title}</div>
			<div className={styles['regulationlist-stats-card-div']}>
				<InputLabel
					sx={{
						textOverflow: 'clip',
						paddingRight:
							displayCount && displayCount.toString().length >= 3 ? '70px' : '',
					}}
					className={styles['regulationlist-stats-card-number']}
				>
					{displayCount}
				</InputLabel>
				<div
					className={
						displayCount && displayCount.toString().length >= 3
							? styles['due-date']
							: ''
					}
				>
					<div className={styles['ticker-icon']}>
						<img src={dropdown} alt="drop-down icon" />
					</div>
					<div className={styles['recent-view']}>
						{viewDataForRange.split(':')[1].trim()}
					</div>
				</div>
			</div>
		</Grid>
	);

	return (
		<div>
			<Grid
				className={styles['regulation-changel-header']}
				data-testid="regulation-change-label"
			>
				<Grid item xs={8}>
					<StyledTextField
						className={styles['regulation-search']}
						InputProps={{
							inputProps: { 'data-testid': 'reg-elastic-search' },
							startAdornment: (
								<InputAdornment position="start">
									<SearchOutlined className={styles['input-search-icon']} />
								</InputAdornment>
							),
						}}
						fullWidth
						id="fullWidth"
						placeholder="Search for a  Regulation"
						onChange={onSearchInputChange}
						defaultValue={refSearchText}
					/>
				</Grid>
				<Typography className={styles['regulation-change-list-heading']}>
					{' '}
					Regulation change list
				</Typography>
				<Card className={styles['regulationlist-stats-card']}>
					<CardContent data-testid="regulation-data">
						{isLoading ? (
							<Loader show={isLoading} loaderText="Loading..." />
						) : (
							<Grid container spacing={5}>
								{finalRuleData &&
									displayCardItems('New', finalRuleData.recentlySent)}
								{finalRuleData &&
									displayCardItems('Past due', finalRuleData.pastDueCount)}
								{finalRuleData &&
									displayCardItems(
										'High priority',
										finalRuleData.highPriorityCount,
									)}
								{finalRuleData &&
									displayCardItems(
										'Deadlines < 30 Days',
										finalRuleData.nearDeadlineCount,
									)}
							</Grid>
						)}
					</CardContent>
				</Card>
				{isAPIDataFetched && <RegulationChangeListTable />}
			</Grid>
			<AlertComponent
				open={isToastOpen}
				toastMessage={toastMessage}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</div>
	);
};

export default RegulationChangeList;
